import axios, { AxiosInstance, AxiosResponse } from 'axios';
import {
  RAGContext,
  QueryRequest,
  QueryResponse,
  UploadResponse,
  ChunkResponse,
  ChunkTool,
  EmbedTask,
  TaskListResponse,
  AskRequest,
  AskResponse,
  PromptData,
  PromptBaseData,
} from '../types/rag';

import { getValidatedApiConfig } from '../utils/apiConfig';

// Get API base URL with validation
const apiConfig = getValidatedApiConfig();
const RAG_API_BASE: string = apiConfig.baseUrl;

class RAGApiService {
  private api: AxiosInstance;
  private baseUrl: string;

  constructor(baseUrl: string = RAG_API_BASE) {
    this.baseUrl = baseUrl;
    this.api = axios.create({
      baseURL: this.baseUrl,
      timeout: 600000,
      withCredentials: false, // Disable credentials for CORS
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });

    // Add request interceptor for debugging
    this.api.interceptors.request.use(
      (config) => {
        return config;
      },
      (error) => {
        console.error('API Request Error:', error);
        return Promise.reject(error);
      }
    );

    // Add response interceptor for debugging and error handling
    this.api.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      (error) => {
        console.error('API Response Error:', error);
        
        // Enhanced error handling for HTTP errors
        if (error.response) {
          const { status, data } = error.response;
          let errorMessage = `HTTP ${status} Error`;
          
          // Extract detailed error information
          if (data && typeof data === 'object') {
            if (data.detail) {
              errorMessage += `: ${data.detail}`;
            } else if (data.message) {
              errorMessage += `: ${data.message}`;
            } else if (data.error) {
              errorMessage += `: ${data.error}`;
            }
          } else if (typeof data === 'string') {
            errorMessage += `: ${data}`;
          }
          
          // Add specific handling for 5xx server errors
          if (status >= 500) {
            errorMessage = `Server Error (${status}): ${errorMessage}`;
          }
          
          // Create enhanced error object
          const enhancedError = new Error(errorMessage);
          (enhancedError as any).status = status;
          (enhancedError as any).data = data;
          (enhancedError as any).isHttpError = true;
          
          return Promise.reject(enhancedError);
        }
        
        // Network or timeout or other errors (no response)
        // Detect axios timeout
        const timeoutMs = (error && error.config && error.config.timeout) || 0;
        const message: string = typeof error?.message === 'string' ? error.message : '';
        if (error.code === 'ECONNABORTED' || message.includes('timeout')) {
          const timeoutError = new Error(`Request Timeout: exceeded ${Math.round((timeoutMs || 0) / 1000)}s`);
          (timeoutError as any).isTimeoutError = true;
          (timeoutError as any).timeoutMs = timeoutMs;
          return Promise.reject(timeoutError);
        }

        if (error.request) {
          const networkError = new Error('Network Error: Unable to connect to server');
          (networkError as any).isNetworkError = true;
          return Promise.reject(networkError);
        }
        
        return Promise.reject(error);
      }
    );
  }

  // Context Management
  async listContexts(): Promise<string[]> {
    try {
      const response = await this.api.get('/rag/list');
      const data = response.data;
      
      // Handle different possible response formats
      if (data.contexts && Array.isArray(data.contexts)) {
        return data.contexts;
      } else if (Array.isArray(data)) {
        return data;
      } else if (typeof data === 'string') {
        return [data];
      }
      
      return [];
    } catch (error) {
      console.error('Failed to list contexts:', error);
      throw new Error(`Failed to list contexts: ${error}`);
    }
  }

  async getContextDetails(contextId: string): Promise<RAGContext> {
    try {
      const response = await this.api.get(`/rag/${contextId}/list`);
      
      // Transform API response to match our TypeScript interface
      const apiData = response.data;
      
      // Transform rag_items to match our RAGItem interface
      const transformedRagItems = (apiData.rag_items || []).map((item: any) => ({
        ragId: item.rag_id,
        fileName: item.file_name,
        fileType: item.file_type,
        filePath: item.file_path,
        updatedAt: item.updated_at,
        fileSize: item.file_size,
        checksum: item.checksum,
        encoding: item.encoding,
        language: item.language,
        status: item.status,
        contentLabels: item.content_labels || []
      }));
      
      const contextData: RAGContext = {
        id: contextId,
        contextId: apiData.context_id || contextId,
        updatedAt: apiData.updated_at || new Date().toISOString(),
        ragItems: transformedRagItems,
        protectedKeywords: apiData.protected_keywords || [],
        keywordMappings: apiData.keyword_mappings || {},
        contextualRetrieval: apiData.contextual_retrieval ?? false
      };

      return contextData;
    } catch (error) {
      console.error('Failed to get context details:', error);
      throw new Error(`Failed to get context details: ${error}`);
    }
  }

  // Toggle Contextual Retrieval for a context. reembed=true re-embeds all rags now.
  async setContextualRetrieval(contextId: string, enabled: boolean, reembed: boolean = false): Promise<any> {
    try {
      const response = await this.api.post(`/rag/${contextId}/contextual`, { enabled, reembed });
      return response.data;
    } catch (error) {
      console.error('Failed to set contextual retrieval:', error);
      throw new Error(`Failed to set contextual retrieval: ${error}`);
    }
  }

  async createContext(contextId: string): Promise<string> {
    try {
      const response = await this.api.post(`/rag/${contextId}/create`);
      return response.data.message || 'Context created successfully';
    } catch (error) {
      console.error('Failed to create context:', error);
      throw new Error(`Failed to create context: ${error}`);
    }
  }

  async deleteContext(contextId: string): Promise<string> {
    try {
      const response = await this.api.delete(`/rag/${contextId}/delete`);
      return response.data.message || 'Context deleted successfully';
    } catch (error) {
      console.error('Failed to delete context:', error);
      throw new Error(`Failed to delete context: ${error}`);
    }
  }

  async deleteRAG(contextId: string, ragId: string): Promise<string> {
    try {
      const response = await this.api.delete(`/rag/${contextId}/${ragId}/delete`);
      return response.data.message || 'RAG deleted successfully';
    } catch (error) {
      console.error('Failed to delete RAG:', error);
      throw new Error(`Failed to delete RAG: ${error}`);
    }
  }

  // File Upload
  async uploadFiles(
    contextId: string,
    files: File[],
    language?: string,
    chunkTool?: ChunkTool
  ): Promise<UploadResponse> {
    try {
      const formData = new FormData();

      files.forEach((file) => {
        formData.append('files', file);
      });

      const queryParams: Record<string, any> = {};
      if (chunkTool) {
        queryParams.chunk_tool = chunkTool.name;
      }
      if (language) {
        queryParams.language = language;
      }

      // Create a separate axios instance for file uploads with proper CORS handling
      const uploadApi = axios.create({
        baseURL: this.baseUrl,
        timeout: 300000, // 5 minutes timeout for file uploads
        withCredentials: false, // Disable credentials for CORS
        headers: {
          // Don't set Content-Type for multipart/form-data, let axios handle it
        },
      });

      const response = await uploadApi.post(
        `/rag/${contextId}/upload/files`,
        formData,
        {
          params: queryParams,
          // Let axios automatically set Content-Type for multipart/form-data
          headers: {
            'Accept': 'application/json',
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to upload files:', error);
      throw new Error(`Failed to upload files: ${error}`);
    }
  }

  // Custom JSON Upload
  async uploadCustomJson(
    contextId: string,
    customText: string,
    language?: string,
    chunkTool?: ChunkTool
  ): Promise<any> {
    try {
      const queryParams: Record<string, any> = {};
      if (chunkTool) {
        queryParams.chunk_tool = chunkTool.name;
      }
      if (language) {
        queryParams.language = language;
      }

      const response = await this.api.post(
        `/rag/${contextId}/upload/custom`,
        {
          custom_text: customText,
          updated_at: new Date().toISOString(),
        },
        { params: queryParams }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to upload custom rag:', error);
      throw new Error(`Failed to upload custom rag: ${error}`);
    }
  }

  // Protected Keywords
  async uploadProtectedKeywords(
    contextId: string,
    protectedKeywords: string[]
  ): Promise<any> {
    try {
      const response = await this.api.post(`/rag/${contextId}/upload/protected`, {
        protected: protectedKeywords,
      });

      return response.data;
    } catch (error) {
      console.error('Failed to upload protected keywords:', error);
      throw new Error(`Failed to upload protected keywords: ${error}`);
    }
  }

  // Keyword Mappings
  async uploadKeywordMappings(
    contextId: string,
    keywordMappings: Record<string, { patterns: string[]; replacement: string[] }>
  ): Promise<any> {
    try {
      const response = await this.api.post(`/rag/${contextId}/upload/keyword-mappings`, {
        mappings: keywordMappings,
      });

      return response.data;
    } catch (error) {
      console.error('Failed to upload keyword mappings:', error);
      throw new Error(`Failed to upload keyword mappings: ${error}`);
    }
  }

  async downloadKeywordMappings(contextId: string): Promise<{ filename: string; data: any }> {
    try {
      const response = await this.api.get(`/rag/${contextId}/download/keyword-mappings`);
      return response.data;
    } catch (error) {
      console.error('Failed to download keyword mappings:', error);
      throw new Error(`Failed to download keyword mappings: ${error}`);
    }
  }

  // Get Chunk Data
  async getChunk(
    contextId: string,
    ragId: string
  ): Promise<ChunkResponse> {
    try {
      const response = await this.api.post(
        `/rag/${contextId}/${ragId}/get_chunk`
      );

      return response.data;
    } catch (error) {
      console.error('Failed to get chunk:', error);
      throw new Error(`Failed to get chunk: ${error}`);
    }
  }

  // Upload Chunk
  async uploadChunk(
    contextId: string,
    ragId: string,
    editChunk?: any
  ): Promise<any> {
    try {

      const response = await this.api.post(
        `/rag/${contextId}/${ragId}/upload/chunk`,
        editChunk || null,
        { timeout: 10000 } // Reduced timeout since it's now a queue operation
      );

      return response.data;
    } catch (error: any) {
      console.error('Failed to upload chunks:', error);
      
      let errorMessage = 'Failed to upload chunks';
      let errorDetails = '';
      
      if (error.response) {
        const status = error.response.status;
        const data = error.response.data;
        
        console.error(`Embed API Error - Status: ${status}, Data:`, data);
        
        if (typeof data === 'string') {
          errorMessage = data;
        } else if (data && typeof data === 'object') {
          if (data.detail) {
            if (typeof data.detail === 'string') {
              errorMessage = data.detail;
            } else if (data.detail.message) {
              errorMessage = data.detail.message;
              errorDetails = `Context: ${data.detail.context_id}, RAG: ${data.detail.rag_id}`;
            } else if (data.detail.error) {
              errorMessage = data.detail.error;
              errorDetails = data.detail.message || '';
            }
          } else if (data.message) {
            errorMessage = data.message;
          } else if (data.error) {
            errorMessage = data.error;
          }
        }
        
        switch (status) {
          case 404:
            errorMessage = `Resource not found: ${errorMessage}`;
            break;
          case 500:
            errorMessage = `Server error during uploading chunks: ${errorMessage}`;
            break;
          default:
            errorMessage = `HTTP ${status}: ${errorMessage}`;
        }
      } else if (error.request) {
        // Request was sent but no response received
        errorMessage = 'No response from server. Please check your connection.';
        console.error('No response received:', error.request);
      } else {
        // Other errors
        errorMessage = error.message || 'Unknown error occurred';
        console.error('Request setup error:', error.message);
      }
      
      // Create error object with detailed information
      const enhancedError = new Error(errorMessage);
      (enhancedError as any).details = errorDetails;
      (enhancedError as any).originalError = error;
      (enhancedError as any).contextId = contextId;
      (enhancedError as any).ragId = ragId;
      
      throw enhancedError;
    }
  }

  // Chunking
  async buildChunk(
    contextId: string,
    ragId: string,
    chunkTool: ChunkTool
  ): Promise<ChunkResponse> {
    try {
      const response = await this.api.post(
        `/rag/${contextId}/${ragId}/chunk`,
        null,
        {
          params: { chunk_tool: chunkTool.name },
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to build chunk:', error);
      throw new Error(`Failed to build chunk: ${error}`);
    }
  }

  // Embedding
  async buildEmbed(
    contextId: string,
    ragId: string,
    editChunk?: any
  ): Promise<any> {
    try {

      // Embed request now returns immediately with task info
      const response = await this.api.post(
        `/rag/${contextId}/${ragId}/embed`,
        editChunk || null,
        { timeout: 10000 } // Reduced timeout since it's now a queue operation
      );

      return response.data;
    } catch (error: any) {
      console.error('Failed to build embed:', error);
      
      // Extract detailed error information
      let errorMessage = 'Failed to build embed';
      let errorDetails = '';
      
      if (error.response) {
        // Server returned an error response
        const status = error.response.status;
        const data = error.response.data;
        
        console.error(`Embed API Error - Status: ${status}, Data:`, data);
        
        if (typeof data === 'string') {
          errorMessage = data;
        } else if (data && typeof data === 'object') {
          if (data.detail) {
            if (typeof data.detail === 'string') {
              errorMessage = data.detail;
            } else if (data.detail.message) {
              errorMessage = data.detail.message;
              errorDetails = `Context: ${data.detail.context_id}, RAG: ${data.detail.rag_id}`;
            } else if (data.detail.error) {
              errorMessage = data.detail.error;
              errorDetails = data.detail.message || '';
            }
          } else if (data.message) {
            errorMessage = data.message;
          } else if (data.error) {
            errorMessage = data.error;
          }
        }
        
        // Add more specific error information based on status code
        switch (status) {
          case 404:
            errorMessage = `Resource not found: ${errorMessage}`;
            break;
          case 500:
            errorMessage = `Server error during embedding: ${errorMessage}`;
            break;
          default:
            errorMessage = `HTTP ${status}: ${errorMessage}`;
        }
      } else if (error.request) {
        // Request was sent but no response received
        errorMessage = 'No response from server. Please check your connection.';
        console.error('No response received:', error.request);
      } else {
        // Other errors
        errorMessage = error.message || 'Unknown error occurred';
        console.error('Request setup error:', error.message);
      }
      
      // Create error object with detailed information
      const enhancedError = new Error(errorMessage);
      (enhancedError as any).details = errorDetails;
      (enhancedError as any).originalError = error;
      (enhancedError as any).contextId = contextId;
      (enhancedError as any).ragId = ragId;
      
      throw enhancedError;
    }
  }

  // Query
  async query(contextId: string, request: QueryRequest): Promise<QueryResponse> {
    try {
      const response = await this.api.post(`/rag/${contextId}/query`, request);
      return response.data;
    } catch (error) {
      console.error('Failed to query:', error);
      throw new Error(`Failed to query: ${error}`);
    }
  }

  // Ask 
  async askSimple(contextId: string, request: AskRequest): Promise<AskResponse> {
    try {
      const response = await this.api.post(`/rag/${contextId}/ask`, request);
      return response.data;
    } catch (error) {
      console.error('Failed to ask: ', error);
      throw new Error(`Failed to ask: ${error}`)
    }
  }

  // Ask with Prompt
  async askWithPrompt(
    contextId: string, 
    prompt: string, 
    filteredChunks: any[], 
    parsedQuery: string, 
    contextLanguage: string
  ): Promise<AskResponse> {
    try {
      const response = await this.api.post(`/rag/${contextId}/ask_with_prompt`, {
        prompt,
        filtered_chunks: filteredChunks,
        parsed_query: parsedQuery,
        context_language: contextLanguage
      });
      return response.data;
    } catch (error) {
      console.error('Failed to ask with prompt:', error);
      throw new Error(`Failed to ask with prompt: ${error}`);
    }
  }

  //Get Prompt Base
  async get_prompt(contextId: string): Promise<PromptBaseData> {
    try {
      const response = await this.api.get(`/rag/${contextId}/prompt`);
      return response.data;
    } catch (error) {
      console.error('Failed to get prompt: ', error);
      throw new Error(`Failed to get prompt: ${error}`)
    }
  }

  //Upload Prompot
  async upload_prompt(contextId: string, edited_prompt: PromptBaseData): Promise<any> {
    try {
      const response = await this.api.post(`/rag/${contextId}/upload/prompt`, edited_prompt);
      return response.data;
    } catch (error) {
      console.error('Failed to upload prompt:', error);
      throw new Error(`Failed to upload prompt: ${error}`);
    }
  }


  // Generate Prompt
  async generate_prompt(contextId: string, request: AskRequest): Promise<PromptData> {
    try {
      const response = await this.api.post(`/rag/${contextId}/generate_prompt`, request);
      return response.data;
    } catch (error) {
      console.error('Failed to generate prompt: ', error);
      throw new Error(`Failed to generate prompt: ${error}`)
    }
  }


  // Get Custom JSON
  async getCustomJson(contextId: string): Promise<any> {
    try {
      const response = await this.api.get(`/rag/${contextId}/custom`);
      return response.data;
    } catch (error) {
      console.error('Failed to get custom RAG:', error);
      throw new Error(`Failed to get custom RAG: ${error}`);
    }
  }

  // Task Queue Management
  async getTaskStatus(taskId: string): Promise<any> {
    try {
      const response = await this.api.get(`/rag/tasks/${taskId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get task status:', error);
      throw new Error(`Failed to get task status: ${error}`);
    }
  }

  async getQueueStatus(): Promise<any> {
    try {
      const response = await this.api.get('/rag/tasks/status');
      return response.data;
    } catch (error) {
      console.error('Failed to get queue status:', error);
      throw new Error(`Failed to get queue status: ${error}`);
    }
  }

  async pollTaskStatus(
    taskId: string, 
    timeout: number = 30, 
    pollInterval: number = 2.0
  ): Promise<any> {
    try {
      const response = await this.api.get(`/rag/tasks/${taskId}/poll`, {
        params: {
          timeout,
          poll_interval: pollInterval
        }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to poll task status:', error);
      throw new Error(`Failed to poll task status: ${error}`);
    }
  }

  async getAllTasks(limit: number = 50): Promise<TaskListResponse> {
    try {
      const response = await this.api.get(`/rag/tasks?limit=${limit}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get all tasks:', error);
      throw new Error(`Failed to get all tasks: ${error}`);
    }
  }

  // Update base URL
  updateBaseUrl(newBaseUrl: string) {
    this.baseUrl = newBaseUrl;
    this.api.defaults.baseURL = newBaseUrl;
  }
}

export default RAGApiService;
